import { getGetConversationQueryKey } from '@roko/api/generated/conversations/conversations'
import type { TChatListItem, TChatMessage } from '@roko/ui/components/chat/types'
import { useQueryClient } from '@tanstack/react-query'
import { type FC, type ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react'
import { flushSync } from 'react-dom'
import { useTranslation } from 'react-i18next'

import {
  useChatQuery,
  useChatsQuery,
  useCreateChatMutation,
  useDeleteChatMutation,
  useResumeChatPromptMutation,
  useSendChatPromptMutation,
} from '@/data/api/hooks/useChat'
import { getUserInitials, mapChatToListItem, mapChatToMessages } from '@/data/api/mappers/chat'
import { type TSafetyGateResponse, submitRegisteredSafetyGate } from '@/data/api/safetyGate'
import { getApiErrorMessage } from '@/data/api/types'
import { useAuthStore } from '@/store/authStore'

type TConversationGateStatus = {
  safety_gate_status?: string
}

type TChatContextValue = {
  chats: TChatListItem[]
  activeChatId?: string
  activeMessages: TChatMessage[]
  hasActiveConversation: boolean
  isLoadingChats: boolean
  isLoadingMessages: boolean
  isSending: boolean
  isEmergencyLock: boolean
  isDangerCheckPending: boolean
  error: string | null
  selectChat: (id: string) => void
  createChat: () => void
  deleteChat: (id: string) => void
  sendMessage: (text: string) => void
  submitSafetyGate: (imminentDanger: boolean) => Promise<TSafetyGateResponse>
  answerDangerCheck: (imminentDanger: boolean) => Promise<void>
  clearEmergencyLock: () => Promise<void>
  clearError: () => void
}

const ChatContext = createContext<TChatContextValue | null>(null)

export const ChatProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const userName = useAuthStore((state) => state.user?.userName)
  const userInitials = useMemo(() => getUserInitials(userName), [userName])

  const [activeChatId, setActiveChatId] = useState<string | undefined>()
  const [pendingMessages, setPendingMessages] = useState<TChatMessage[]>([])
  /** Set from SSE [DANGER_CHECK]; kept until server status catches up or user answers. */
  const [streamDangerPending, setStreamDangerPending] = useState(false)
  const [streamEmergencyLock, setStreamEmergencyLock] = useState(false)

  const chatsQuery = useChatsQuery()
  const chatQuery = useChatQuery(activeChatId)
  const createChatMutation = useCreateChatMutation()
  const deleteChatMutation = useDeleteChatMutation()
  const sendChatPromptMutation = useSendChatPromptMutation()
  const resumeChatPromptMutation = useResumeChatPromptMutation()

  const switchActiveChat = useCallback((id: string | undefined) => {
    setActiveChatId(id)
    setPendingMessages([])
    setStreamDangerPending(false)
    setStreamEmergencyLock(false)
  }, [])

  const chats = useMemo(() => {
    return (chatsQuery.data ?? []).map((chat) => {
      const item = mapChatToListItem(chat)

      if (item.title === 'New chat') {
        return { ...item, title: t('chat.newChat') }
      }

      return item
    })
  }, [chatsQuery.data, t])

  const safetyGateStatus = (chatQuery.data as TConversationGateStatus | undefined)?.safety_gate_status
  const isDangerCheckPending = safetyGateStatus === 'pending' || streamDangerPending
  const isEmergencyLock = safetyGateStatus === 'emergency' || streamEmergencyLock

  const activeMessages = useMemo(() => {
    const messages = chatQuery.data ? mapChatToMessages(chatQuery.data, userInitials) : []

    return [...messages, ...pendingMessages]
  }, [chatQuery.data, pendingMessages, userInitials])

  const hasActiveConversation = activeMessages.length > 0

  const error = useMemo(() => {
    if (chatsQuery.isError) {
      return getApiErrorMessage(chatsQuery.error, t('chat.errors.loadChats'))
    }

    if (chatQuery.isError) {
      return getApiErrorMessage(chatQuery.error, t('chat.errors.loadMessages'))
    }

    if (createChatMutation.isError) {
      return getApiErrorMessage(createChatMutation.error, t('chat.errors.createChat'))
    }

    if (deleteChatMutation.isError) {
      return getApiErrorMessage(deleteChatMutation.error, t('chat.errors.deleteChat'))
    }

    if (sendChatPromptMutation.isError || resumeChatPromptMutation.isError) {
      return getApiErrorMessage(sendChatPromptMutation.error ?? resumeChatPromptMutation.error, t('chat.errors.sendMessage'))
    }

    return null
  }, [
    chatQuery.error,
    chatQuery.isError,
    chatsQuery.error,
    chatsQuery.isError,
    createChatMutation.error,
    createChatMutation.isError,
    deleteChatMutation.error,
    deleteChatMutation.isError,
    resumeChatPromptMutation.error,
    resumeChatPromptMutation.isError,
    sendChatPromptMutation.error,
    sendChatPromptMutation.isError,
    t,
  ])

  const clearError = useCallback(() => {
    createChatMutation.reset()
    deleteChatMutation.reset()
    sendChatPromptMutation.reset()
    resumeChatPromptMutation.reset()
  }, [createChatMutation, deleteChatMutation, resumeChatPromptMutation, sendChatPromptMutation])

  const selectChat = useCallback(
    (id: string) => {
      switchActiveChat(id)
    },
    [switchActiveChat]
  )

  const createChat = useCallback(() => {
    createChatMutation.mutate(
      { data: null },
      {
        onSuccess: (chat) => {
          switchActiveChat(chat.id)
        },
      }
    )
  }, [createChatMutation, switchActiveChat])

  const deleteChat = useCallback(
    (id: string) => {
      deleteChatMutation.mutate(
        { conversationId: id },
        {
          onSuccess: () => {
            if (activeChatId !== id) {
              return
            }

            const remainingChats = chats.filter((chat) => chat.id !== id)
            switchActiveChat(remainingChats[0]?.id)
          },
        }
      )
    },
    [activeChatId, chats, deleteChatMutation, switchActiveChat]
  )

  const sendMessage = useCallback(
    (text: string) => {
      if (sendChatPromptMutation.isPending || resumeChatPromptMutation.isPending || createChatMutation.isPending) {
        return
      }

      const timestamp = Date.now()
      const pendingAssistantId = `pending-assistant-${timestamp}`
      setPendingMessages([
        {
          id: `pending-user-${timestamp}`,
          role: 'user',
          authorInitials: userInitials,
          content: text,
        },
        {
          id: pendingAssistantId,
          role: 'assistant',
          authorInitials: 'AI',
          isPending: true,
          content: '',
        },
      ])

      const sendToConversation = (conversationId: string) => {
        sendChatPromptMutation.mutate(
          {
            conversationId,
            data: { content: text },
            onChunk: (chunk) => {
              flushSync(() => {
                setPendingMessages((current) =>
                  current.map((message) =>
                    message.id === pendingAssistantId
                      ? { ...message, content: `${message.content ?? ''}${chunk}`, isPending: true }
                      : message
                  )
                )
              })
            },
            onEmergency: () => {
              setStreamEmergencyLock(true)
              setStreamDangerPending(false)
            },
            onDangerCheck: () => {
              setStreamDangerPending(true)
              setStreamEmergencyLock(false)
            },
          },
          { onSettled: () => setPendingMessages([]) }
        )
      }

      if (activeChatId) {
        sendToConversation(activeChatId)
        return
      }

      createChatMutation.mutate(
        { data: null },
        {
          onSuccess: (chat) => {
            setActiveChatId(chat.id)
            sendToConversation(chat.id)
          },
          onError: () => {
            setPendingMessages([])
          },
        }
      )
    },
    [activeChatId, createChatMutation, resumeChatPromptMutation.isPending, sendChatPromptMutation, userInitials]
  )

  const submitSafetyGate = useCallback(
    async (imminentDanger: boolean): Promise<TSafetyGateResponse> => {
      let conversationId = activeChatId
      if (!conversationId) {
        const chat = await createChatMutation.mutateAsync({ data: null })
        conversationId = chat.id
        switchActiveChat(chat.id)
      }
      const result = await submitRegisteredSafetyGate(conversationId, imminentDanger)
      if (imminentDanger) {
        setStreamEmergencyLock(true)
        setStreamDangerPending(false)
      } else {
        setStreamEmergencyLock(false)
        setStreamDangerPending(false)
      }
      await queryClient.invalidateQueries({ queryKey: getGetConversationQueryKey(conversationId) })
      return result
    },
    [activeChatId, createChatMutation, queryClient, switchActiveChat]
  )

  const answerDangerCheck = useCallback(
    async (imminentDanger: boolean) => {
      if (!activeChatId) {
        return
      }

      await submitSafetyGate(imminentDanger)
      if (imminentDanger) {
        return
      }

      const timestamp = Date.now()
      const pendingAssistantId = `pending-assistant-resume-${timestamp}`
      setPendingMessages([
        {
          id: pendingAssistantId,
          role: 'assistant',
          authorInitials: 'AI',
          isPending: true,
          content: '',
        },
      ])

      try {
        await resumeChatPromptMutation.mutateAsync({
          conversationId: activeChatId,
          onChunk: (chunk) => {
            flushSync(() => {
              setPendingMessages((current) =>
                current.map((message) =>
                  message.id === pendingAssistantId ? { ...message, content: `${message.content ?? ''}${chunk}`, isPending: true } : message
                )
              )
            })
          },
        })
      } finally {
        setPendingMessages([])
      }
    },
    [activeChatId, resumeChatPromptMutation, submitSafetyGate]
  )

  const clearEmergencyLock = useCallback(async () => {
    await submitSafetyGate(false)
  }, [submitSafetyGate])

  const value = useMemo(
    () => ({
      chats,
      activeChatId,
      activeMessages,
      hasActiveConversation,
      isLoadingChats: chatsQuery.isPending,
      isLoadingMessages: Boolean(activeChatId) && chatQuery.isPending,
      isSending: sendChatPromptMutation.isPending || resumeChatPromptMutation.isPending || createChatMutation.isPending,
      isEmergencyLock,
      isDangerCheckPending,
      error,
      selectChat,
      createChat,
      deleteChat,
      sendMessage,
      submitSafetyGate,
      answerDangerCheck,
      clearEmergencyLock,
      clearError,
    }),
    [
      activeChatId,
      activeMessages,
      answerDangerCheck,
      chatQuery.isPending,
      chats,
      chatsQuery.isPending,
      clearEmergencyLock,
      clearError,
      createChat,
      createChatMutation.isPending,
      deleteChat,
      error,
      hasActiveConversation,
      isDangerCheckPending,
      isEmergencyLock,
      resumeChatPromptMutation.isPending,
      selectChat,
      sendChatPromptMutation.isPending,
      sendMessage,
      submitSafetyGate,
    ]
  )

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>
}

export const useChatContext = (): TChatContextValue => {
  const context = useContext(ChatContext)
  if (!context) {
    throw new Error('useChatContext must be used within ChatProvider')
  }
  return context
}
